import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import SocialLoginButton from '../components/common/SocialLoginButton';

const RegisterPage: React.FC = () => {
  const { signup, loginWithGoogle, loginWithFacebook } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Store redirect path for after login
    const searchParams = new URLSearchParams(location.search);
    const redirectAfterAuth = searchParams.get('redirect') || '/';
    sessionStorage.setItem('redirectAfterAuth', redirectAfterAuth);

    // Check for auth errors from OAuth callback
    const authError = searchParams.get('error');
    if (authError) {
      setError(t('auth.socialLoginError'));
    }
  }, [location.search, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (formData.password !== formData.confirmPassword) {
        throw new Error(t('auth.passwordsDoNotMatch'));
      }
      const { requiresEmailConfirmation } = await signup(formData.name, formData.email, formData.password);

      if (requiresEmailConfirmation) {
        setSuccess(true);
        // Redirect after successful registration
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        const redirectTo = sessionStorage.getItem('redirectAfterAuth') || '/';
        sessionStorage.removeItem('redirectAfterAuth');
        navigate(redirectTo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setError('');
    setIsLoading(true);

    try {
      if (provider === 'google') {
        await loginWithGoogle();
      } else if (provider === 'facebook') {
        await loginWithFacebook();
      }
      // OAuth redirect will handle the rest
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.socialLoginError'));
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-soft sm:rounded-2xl sm:px-10 border border-neutral-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                Registracija uspešna!
              </h2>
              <p className="text-neutral-600 mb-6">
                Proverite svoj email da potvrdite nalog, a zatim se prijavite.
              </p>
              <p className="text-sm text-neutral-500">
                Prebacujem vas na stranicu za prijavu...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex items-center justify-center mb-8">
          <img
            src="/stanorama-logo.svg"
            alt="Stanorama"
            className="h-12 w-auto"
          />
        </div>
        <h2 className="text-center text-3xl font-extrabold text-neutral-900 mb-2">
          {t('auth.signUp')}
        </h2>
        <p className="text-center text-sm text-neutral-600">
          {t('auth.signUpSubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-soft sm:rounded-2xl sm:px-10 border border-neutral-200">
          {/* Social Login Section - TEMPORARILY DISABLED */}
          {/* <div className="space-y-3">
            <SocialLoginButton
              provider="google"
              onClick={() => handleSocialLogin('google')}
              disabled={isLoading}
            />
            <SocialLoginButton
              provider="facebook"
              onClick={() => handleSocialLogin('facebook')}
              disabled={isLoading}
            />
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-neutral-500">
                  {t('auth.orContinueWith')}
                </span>
              </div>
            </div>
          </div> */}

          {/* Email/Password Form */}
          <form className="space-y-6 mt-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-neutral-700">
                {t('auth.name')}
              </label>
              <div className="mt-1">
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('auth.enterName')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700">
                {t('auth.email')}
              </label>
              <div className="mt-1">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('auth.enterEmail')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
                {t('auth.password')}
              </label>
              <div className="mt-1">
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.enterPassword')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700">
                {t('auth.confirmPassword')}
              </label>
              <div className="mt-1">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">
                  {error}
                </div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                fullWidth
                disabled={isLoading}
                className="justify-center"
              >
                {isLoading ? t('auth.processing') : t('auth.signUp')}
              </Button>
            </div>
          </form>

          {/* Login link */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-neutral-500">
                  {t('auth.haveAccount')}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <Link to="/login">
                <Button
                  variant="outline"
                  fullWidth
                  disabled={isLoading}
                  className="justify-center"
                >
                  {t('auth.signIn')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
